import { useState } from "react";
import { Info, Pencil } from "lucide-react";
import { motion } from "motion/react";
import { SalaryData } from "../../../types/SalaryData";
import { MarketData } from "../../../types/MarketData";
import { Skeleton } from "./ui/skeleton";

interface OptimizedResultCardProps {
  salaryData: SalaryData;
  marketData: MarketData | null;
  isCalculating: boolean;
  onAdjustData?: () => void;
}

function getVerdict(diffPercent: number, isClose: boolean) {
  if (isClose) return {
    label: "Midt i markedet",
    description: "Din nuværende løn ligger inden for det typiske marked",
    color: "#008e9f",
    position: 50,
  };
  if (diffPercent > 10) return {
    label: "Højt i markedet",
    description: "Din nuværende løn ligger i den høje ende af markedet",
    color: "#059669",
    position: 85,
  };
  if (diffPercent > 0) return {
    label: "Over markedet",
    description: "Din nuværende løn ligger lidt over det typiske marked",
    color: "#10b981",
    position: 65,
  };
  if (diffPercent > -10) return {
    label: "Under markedet",
    description: "Din nuværende løn ligger lidt under det typiske marked",
    color: "#f59e0b",
    position: 35,
  };
  return {
    label: "Lavt i markedet",
    description: "Din nuværende løn ligger i den lave ende af markedet",
    color: "#ef4444",
    position: 15,
  };
}

function getDrivers(salaryData: SalaryData) {
  const drivers: string[] = [];
  if (salaryData.experience !== null) {
    if (salaryData.experience >= 10) drivers.push("Din erfaring trækker lønnen op");
    else if (salaryData.experience <= 2) drivers.push("Begrænset erfaring påvirker lønnen");
  }
  if (salaryData.education === "Lang") drivers.push("Lang uddannelse giver lønpræmie");
  if (salaryData.education === "Grundskole") drivers.push("Uddannelsesniveau påvirker lønnen");
  if (salaryData.region === "Hovedstaden") drivers.push("Hovedstaden har højere lønniveau");
  if (salaryData.region === "Nordjylland") drivers.push("Nordjylland har lavere lønniveau");
  if (drivers.length === 0) drivers.push("Jobtitel og erfaring er de største lønfaktorer");
  return drivers.slice(0, 3);
}

function FairnessBarometer({ verdict, salary, marketData }: {
  verdict: ReturnType<typeof getVerdict>;
  salary: number;
  marketData: MarketData;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isVeryLowData = marketData.sampleSize < 5;

  return (
    <div className="bg-white rounded-2xl border border-[#f3f4f6] px-6 py-6">
      {/* Header with status badge */}
      <div className="flex items-start justify-between mb-[21px]">
        <h3 className="text-[17px] font-bold text-[#1a1a1a] leading-[25.5px]">Sådan er din løn</h3>
        <div
          className="inline-block px-3 py-[5.14px] rounded-[10px] text-[13px] font-bold leading-[19.5px]"
          style={{
            backgroundColor: verdict.color === '#ef4444' ? '#fef2f2' :
                           verdict.color === '#f59e0b' ? '#fffbeb' :
                           verdict.color === '#008e9f' ? '#e6f4f5' :
                           verdict.color === '#10b981' ? '#f0fdf4' : '#f0fdf4',
            color: verdict.color
          }}
        >
          {verdict.label}
        </div>
      </div>

      {/* 5-level fairness bar */}
      <div className="mb-2">
        <div className="relative h-[10px] rounded-full overflow-visible bg-gradient-to-r from-[#fb2c36] via-[#00d492] to-[#096]">
          {/* Position marker - white bar */}
          <motion.div
            className="absolute -top-1 -translate-x-1/2 z-10"
            style={{ left: `${verdict.position}%` }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 20L1 1H23L12 20Z" fill="white" stroke="#9ca3af" strokeWidth="2"/>
            </svg>
          </motion.div>
        </div>

        {/* Scale labels */}
        <div className="flex items-center justify-between px-1 mt-2">
          <span className="text-[12px] font-medium text-[#6b7280] leading-[18px]">Lavt</span>
          <span className="text-[12px] font-medium text-[#6b7280] leading-[18px]">Under</span>
          <span className="text-[12px] font-medium text-[#6b7280] leading-[18px]">Midt</span>
          <span className="text-[12px] font-medium text-[#6b7280] leading-[18px]">Over</span>
          <span className="text-[12px] font-medium text-[#6b7280] leading-[18px]">Højt</span>
        </div>
      </div>

      {/* Current salary */}
      <motion.div
        className="mt-[24px]"
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-medium text-[#3a3a39] leading-[21px]">Din nuværende bruttoløn</span>
          <span className="text-[15px] font-bold text-[#1a1a1a] tabular-nums leading-[22.5px]">{salary.toLocaleString("da-DK")} kr.</span>
        </div>
      </motion.div>

      {/* Market data */}
      <div className="mt-[14px] space-y-[14px]">
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-medium text-[#3a3a39] leading-[21px]">Markedsgennemsnit</span>
          <span className="text-[15px] font-bold text-[#1a1a1a] tabular-nums leading-[22.5px]">{marketData.average.toLocaleString("da-DK")} kr.</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[14px] font-medium text-[#3a3a39] leading-[21px]">Typisk løninterval</span>
          <span className="text-[14px] font-bold text-[#3a3a39] tabular-nums leading-[21px]">{marketData.rangeMin.toLocaleString("da-DK")}–{marketData.rangeMax.toLocaleString("da-DK")} kr.</span>
        </div>
      </div>

      {/* Divider before datagrundlag */}
      <div className="h-px bg-[#e5e7eb] mt-[14px] mb-[14px]" />

      {/* Datagrundlag */}
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-[#3a3a39] leading-[21px]">Datagrundlag</span>
        {isVeryLowData ? (
          <div className="flex items-center gap-1.5 relative">
            <span className="text-[12px] font-medium text-[#3a3a39] leading-[21px]">Begrænset</span>
            <div
              className="relative"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info className="w-3.5 h-3.5 text-[#6b7280] cursor-help" />
              {showTooltip && (
                <div className="absolute right-0 bottom-full mb-2 w-[280px] bg-[#1a1a1a] text-white text-[12px] rounded-lg px-3 py-2.5 shadow-lg z-50">
                  <div className="absolute right-3 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#1a1a1a]" />
                  Vi har fundet færre relevante profiler end normalt for denne beregning. Derfor er resultatet vejledende og baseret på et bredere sammenligningsgrundlag.
                </div>
              )}
            </div>
          </div>
        ) : (
          <span className="text-[12px] font-medium text-[#3a3a39] leading-[21px]">{marketData.sampleSize} profiler</span>
        )}
      </div>
    </div>
  );
}

export function OptimizedResultCard({ salaryData, marketData, isCalculating, onAdjustData }: OptimizedResultCardProps) {
  const hasData = salaryData.jobTitle && salaryData.salary > 0;

  if (!hasData) return null;

  if (isCalculating) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-gray-100 p-6">
          <Skeleton className="h-8 w-32 mb-4" />
          <Skeleton className="h-12 w-full rounded-full mb-4" />
          <Skeleton className="h-6 w-40 mb-2" />
          <Skeleton className="h-4 w-full" />
        </div>
      </div>
    );
  }

  if (!marketData || marketData.sampleSize === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="bg-white rounded-2xl border border-[#fecaca] bg-[#fef2f2] px-4 py-6 sm:px-6 sm:py-8 w-full"
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#fee2e2] flex items-center justify-center">
            <svg className="w-6 h-6 text-[#ef4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-[16px] font-bold text-[#1a1a1a] mb-1">Ingen resultater fundet</h3>
            <p className="text-[14px] text-[#6b7280] mb-4">
              Vi kunne ikke finde løndata for dine valgte kriterier. Prøv at ændre dine valg for at få et resultat.
            </p>
             <p className="text-[14px] text-[#6b7280] mb-4">
              Det sker desværre en sjælden imellem at der ikke er tilstrækkelig data tilgængelig for din stillingsbetegnelse.
            </p>
            {onAdjustData && (
              <button
                onClick={onAdjustData}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#ef4444] text-white font-medium rounded-lg hover:bg-[#dc2626] transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Rediger valg
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  const diff = salaryData.salary - marketData.average;
  const diffPercent = Math.round((diff / marketData.average) * 100);
  const isClose = Math.abs(diffPercent) <= 3;

  const verdict = getVerdict(diffPercent, isClose);
  const drivers = getDrivers(salaryData);
  const isLowData = marketData.sampleSize < 25;

  return (
    <motion.div
      key="optimized-result"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="space-y-4"
    >
      {/* Fairness barometer with market data */}
      <FairnessBarometer verdict={verdict} salary={salaryData.salary} marketData={marketData} />
    </motion.div>
  );
}